import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { Box, CircularProgress, List, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useGetThreadsByUser } from "../../hooks/useGetThreadsByUser";
import { filterByCourse } from "../../utils/filterByCourse";
import { ThreadListItem } from "./ThreadListItem";
import { useGetQuizzesByUser } from "../../hooks/useGetQuizzesByUser";

export const ThreadList = ({
  createdNewThread,
  loadingCreateThread,
  isTyping,
  selectedCourse,
  currentThread,
  onThreadClick,
  handleMenuOpen,
  theme,
  isMobile,
  handleRenameThread,
  handleDeleteThread,
  user,
  isAdmin,
  setCurrentThread,
  userThreads
}) => {
  const [filteredThreads, setFilteredThreads] = useState([]);

  const {
    mutate: getThreads,
    isLoading,
    isError,
    error,
    data,
  } = useGetThreadsByUser();

  const {
    mutate: getQuizzes,
    isLoading: isQuizzesLoading,
    isError: isQuizzesError,
    data: quizzesData,
  } = useGetQuizzesByUser();


  const refetchQuizzes = useCallback(() => {
    if (!isAdmin) {
      getQuizzes(user.userId);
    }
  }, [getQuizzes, user, isAdmin]);

  const refetchThreads = useCallback(() => {
    if (!isAdmin) {
      getThreads(user.userId);
    } 
  }, [getThreads, user, isAdmin]);

  useEffect(() => {
    refetchThreads();
    refetchQuizzes();
  }, [refetchThreads,refetchQuizzes, createdNewThread]);

  useEffect(() => {
    if (!userThreads) {
      const newFilteredThreads = filterByCourse(selectedCourse, data);
      setFilteredThreads(newFilteredThreads);
    } else {
      setFilteredThreads(userThreads);
    }
  }, [user, data, selectedCourse, userThreads]);

  useEffect(() => {
    if (data && quizzesData) {
      const combinedThreads = [...data, ...quizzesData];
      const newFilteredThreads = filterByCourse(selectedCourse, combinedThreads);
      setFilteredThreads(newFilteredThreads);
    }
  }, [user, data, quizzesData, selectedCourse]);

  const handleDeleteThreadMemo = (threadToDelete) => {
    const id = threadToDelete.threadId || threadToDelete.quizId;
    setFilteredThreads((prevThreads) =>
      prevThreads.filter((thread) => (thread.threadId || thread.quizId) !== id)
    );
    handleDeleteThread(threadToDelete);
  };

  const handleRenameThreadMemo = (threadToRename, newName) => {
    setFilteredThreads((prevThreads) =>
      prevThreads.map((thread) => {
        const id = thread.threadId || thread.quizId;
        if (id === (threadToRename.threadId || threadToRename.quizId)) {
          return { ...thread, threadName: newName };
        }
        return thread;
      })
    );
    handleRenameThread(threadToRename, newName);
  };

  // Error handling
  if (isError) {
    return (
      <Typography color="error">
        Error fetching threads: {error.message}
      </Typography>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Empty state handling
  if (filteredThreads.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="70%"
        textAlign="center"
        padding={1}
        marginTop={-15}
      >
        {isMobile && (
          <>
            <AddCircleOutlineIcon fontSize="large" color="action" />
            <Typography variant="h6" color="textSecondary" marginTop={2}>
              No threads available
            </Typography>
            <Typography variant="body1" color="textSecondary" marginTop={1}>
              Send your first message.
            </Typography>
          </>
        )}

        {!isMobile && (
          <>
            <AddCircleOutlineIcon fontSize="large" color="action" />
            <Typography variant="h6" color="textSecondary" marginTop={2}>
              No threads available for this course
            </Typography>
            <Typography variant="body1" color="textSecondary" marginTop={1}>
              Start a new conversation by sending your first message.
            </Typography>
          </>
        )}
      </Box>
    );
  }

  return (
    <List sx={{ overflowY: "auto", flexGrow: 1, position: "relative" }}>
      {filteredThreads.map((thread, index) => (
        <ThreadListItem
          isTyping={isTyping}
          loadingCreateThread={loadingCreateThread}
          key={thread.threadId}
          thread={thread}
          index={index}
          currentThread={currentThread}
          onThreadClick={onThreadClick}
          handleMenuOpen={handleMenuOpen}
          theme={theme}
          isMobile={isMobile}
          handleRenameThread={handleRenameThreadMemo}
          handleDeleteThread={handleDeleteThreadMemo}
          setSelectedThread={setCurrentThread}
        />
      ))}
    </List>
  );
};
